/**
 * Express API Adapter — Calls self-hosted Express server
 * Sprint 4, Story 4.7
 *
 * Replaces supabase.functions.invoke() with fetch('/api/...').
 * Swap this adapter in src/lib/api/index.ts to switch from Supabase to Express.
 */

import type { ApiClient, ApiInvokeOptions, ApiResult } from './types';
import { handleUnauthorized } from './session-guard';

const API_BASE_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3001';

class ExpressApiAdapter implements ApiClient {
  private getAuthToken(): string | null {
    try {
      return localStorage.getItem('auth_token');
    } catch {
      return null;
    }
  }

  async invoke<T = any>(functionName: string, options?: ApiInvokeOptions): Promise<ApiResult<T>> {
    try {
      const token = this.getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options?.headers,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/${functionName}`, {
        method: 'POST',
        headers,
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });

      // Defensive parse: response.json() lança SyntaxError "Unexpected token"
      // quando o body não é JSON (HTML 502/504 do nginx, redirect, body
      // parser intermitente). Item 2 reunião Walter 2026-05-06.
      const text = await response.text();
      let data: any = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        const ct = response.headers.get('content-type') || 'unknown';
        const preview = text.slice(0, 120).replace(/\s+/g, ' ');
        console.error(
          `[api-adapter] resposta não-JSON em /api/${functionName}: status=${response.status} content-type=${ct} body[0..120]="${preview}"`,
        );
        return {
          data: null,
          error: {
            message: `Servidor retornou resposta inválida (HTTP ${response.status}). Tente novamente — se persistir, contate suporte.`,
            context: { status: response.status, contentType: ct, bodyPreview: preview },
          },
        };
      }

      if (!response.ok) {
        // Mesma regra do fetchApi e do adapter de /api/db (CLAUDE.md §10): token
        // recusado encerra a sessão em vez de virar tela vazia.
        if (response.status === 401) handleUnauthorized(`/api/${functionName}`);
        return {
          data: null,
          error: {
            message: data?.error?.message || data?.error || `HTTP ${response.status}`,
            context: data,
          },
        };
      }

      // Compatibilidade: algumas respostas retornam { data, error } e outras retornam direto
      if (data && typeof data === 'object' && 'data' in data) {
        return { data: data.data as T, error: data.error || null };
      }

      return { data: data as T, error: null };
    } catch (error: any) {
      return {
        data: null,
        error: { message: error.message || 'Network error' },
      };
    }
  }
}

export const expressApiAdapter = new ExpressApiAdapter();
